import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import type { FC, ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { EDR_COLORS, edrCardSx } from './edrTokens';

export type EdrEntityDetailPageProps = {
  backLink: { label: string; to: string };
  title: string;
  badges?: ReactNode;
  metaLine?: ReactNode;
  main: ReactNode;
  sidebar?: ReactNode;
  loading?: boolean;
  notFoundMessage?: string;
};

export const EdrEntityDetailPage: FC<EdrEntityDetailPageProps> = ({
  backLink,
  title,
  badges,
  metaLine,
  main,
  sidebar,
  loading = false,
  notFoundMessage,
}) => {
  const theme = useTheme();

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16, minWidth: 0 }}>
      <RouterLink
        to={backLink.to}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          alignSelf: 'flex-start',
          color: theme.palette.primary.main,
          textDecoration: 'none',
          fontSize: 13,
          fontWeight: 500,
          fontFamily: 'Inter, sans-serif',
        }}
      >
        <ArrowBackIcon style={{ fontSize: 18 }} />
        {backLink.label}
      </RouterLink>

      {loading ? (
        <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
          <Skeleton variant="text" width="40%" height={32} />
          <Skeleton variant="text" width="50%" height={20} />
          <Box display="flex" style={{ gap: 16, flexWrap: 'wrap' }}>
            <Box flex="2 1 480px" display="flex" flexDirection="column" style={{ gap: 12 }}>
              <Skeleton variant="rectangular" height={88} style={{ borderRadius: 8 }} />
              <Skeleton variant="rectangular" height={160} style={{ borderRadius: 8 }} />
              <Skeleton variant="rectangular" height={120} style={{ borderRadius: 8 }} />
            </Box>
            <Box flex="1 1 280px">
              <Skeleton variant="rectangular" height={200} style={{ borderRadius: 8 }} />
            </Box>
          </Box>
        </Box>
      ) : notFoundMessage ? (
        <Card sx={edrCardSx}>
          <CardContent style={{ padding: 24 }}>
            <Typography style={{ fontSize: 14, color: EDR_COLORS.textSecondary }}>
              {notFoundMessage}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Box minWidth={0}>
            <Box display="flex" alignItems="flex-start" flexWrap="wrap" style={{ gap: 8 }}>
              <Typography
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 20,
                  fontWeight: 700,
                  color: EDR_COLORS.textPrimary,
                  lineHeight: 1.3,
                  wordBreak: 'break-word',
                  flex: '1 1 240px',
                  minWidth: 0,
                }}
              >
                {title}
              </Typography>
              {badges ? (
                <Box display="flex" alignItems="center" style={{ gap: 8, paddingTop: 2 }}>
                  {badges}
                </Box>
              ) : null}
            </Box>
            {metaLine ? (
              <Typography
                component="div"
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 12,
                  color: EDR_COLORS.textSecondary,
                  marginTop: 6,
                  lineHeight: 1.5,
                }}
              >
                {metaLine}
              </Typography>
            ) : null}
          </Box>

          <Box
            display="flex"
            alignItems="flex-start"
            style={{ gap: 14, flexWrap: 'wrap', width: '100%' }}
          >
            <Box
              display="flex"
              flexDirection="column"
              style={{ flex: '1.2 1 420px', gap: 12, minWidth: 0 }}
            >
              {main}
            </Box>
            {sidebar ? (
              <Box
                display="flex"
                flexDirection="column"
                style={{
                  flex: '1 1 320px',
                  gap: 12,
                  minWidth: 0,
                  maxWidth: '100%',
                  position: 'sticky',
                  top: 16,
                }}
              >
                {sidebar}
              </Box>
            ) : null}
          </Box>
        </>
      )}
    </Box>
  );
};
